// ============================================================
// Datei: frontend/src/components/Toast.tsx
// Toast Notification System — schöne Fehlermeldungen
// ============================================================
import React, { useEffect, useState, useCallback, createContext, useContext } from 'react';

// ============================================================
// Types
// ============================================================
type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: number;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextType {
  showToast: (type: ToastType, title: string, message?: string, duration?: number) => void;
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast muss in ToastProvider sein');
  return ctx;
};

// ============================================================
// Styles
// ============================================================
const typeConfig: Record<ToastType, { icon: string; bg: string; border: string; accent: string }> = {
  success: { icon: '✅', bg: '#0f2a1e', border: '#22c55e44', accent: '#22c55e' },
  error:   { icon: '❌', bg: '#2a0f0f', border: '#ef444444', accent: '#ef4444' },
  warning: { icon: '⚠️', bg: '#2a2510', border: '#f59e0b44', accent: '#f59e0b' },
  info:    { icon: 'ℹ️', bg: '#0f1a2a', border: '#3b82f644', accent: '#3b82f6' },
};

// ============================================================
// Single Toast
// ============================================================
function ToastNotification({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const config = typeConfig[toast.type];
  const duration = toast.duration || 5000;

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onDismiss(toast.id), 300);
    }, duration);
    return () => clearTimeout(timer);
  }, [toast.id, duration, onDismiss]);

  return (
    <div
      style={{
        background: config.bg,
        border: `1px solid ${config.border}`,
        borderLeft: `4px solid ${config.accent}`,
        borderRadius: 12,
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        minWidth: 320,
        maxWidth: 440,
        boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
        transform: visible && !exiting ? 'translateX(0)' : 'translateX(120%)',
        opacity: visible && !exiting ? 1 : 0,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        position: 'relative' as const,
      }}
      onClick={() => { setExiting(true); setTimeout(() => onDismiss(toast.id), 300); }}
    >
      <span style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>{config.icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ color: config.accent, fontSize: 14, fontWeight: 700 }}>{toast.title}</div>
        {toast.message && (
          <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4, lineHeight: 1.4 }}>
            {toast.message}
          </div>
        )}
      </div>
      <span style={{ color: '#475569', fontSize: 16, cursor: 'pointer', marginTop: -2 }}>✕</span>
      {/* Progress bar */}
      <div style={{
        position: 'absolute', bottom: 0, left: 4, right: 0, height: 3, borderRadius: '0 0 12px 12px', overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          background: config.accent,
          opacity: 0.5,
          animation: `shrink ${duration}ms linear forwards`,
        }} />
      </div>
    </div>
  );
}

// ============================================================
// Toast Provider
// ============================================================
let toastId = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback((type: ToastType, title: string, message?: string, duration?: number) => {
    const id = ++toastId;
    setToasts((prev) => [...prev.slice(-4), { id, type, title, message, duration }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value: ToastContextType = {
    showToast,
    success: (t, m) => showToast('success', t, m),
    error: (t, m) => showToast('error', t, m),
    warning: (t, m) => showToast('warning', t, m),
    info: (t, m) => showToast('info', t, m),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast Container */}
      <div style={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        pointerEvents: 'none',
      }}>
        {toasts.map((t) => (
          <div key={t.id} style={{ pointerEvents: 'auto' }}>
            <ToastNotification toast={t} onDismiss={dismiss} />
          </div>
        ))}
      </div>
      {/* Animation keyframe */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </ToastContext.Provider>
  );
};

export default ToastContext;